/**
 * EAA-1988: blocks the whole app while the cơ sở is being renamed, and shows how
 * far along it is.
 *
 * ACH holds a tenant offline for the duration of a slug rename, refusing every
 * request with 409. A banner would understate that — the screen behind it is not
 * usable, every click returns an error, and a user would keep trying. So the app
 * is covered and blurred, and the reason sits in the middle of it.
 *
 * The state is owned by the API error middleware rather than any page, because
 * the refusal can arrive on any request; see `store/tenantRenameStore.ts`. While
 * the lock is up this component polls `GET /tenants/me` — the one endpoint that
 * stays reachable during the lockdown — because a blocked app makes no other
 * requests, so without a poll the progress shown would be frozen at whatever the
 * refusal that raised the lock happened to say.
 *
 * When the rename ends this component also refetches the app's data. Everything
 * the user was looking at failed while the tenant was offline, so uncovering the
 * screen without refetching hands them the errors the overlay was hiding.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryClient } from '@/lib/queryClient';
import { tenantService } from '@/services/tenant.service';
import {
    parseTenantRenameInfo,
    useTenantRenameStore,
    type TenantRenameInfo,
} from '@/store/tenantRenameStore';

const BODY_LOCK_CLASS = 'tenant-rename-locked';

/** How often to ask for progress. Slow enough not to matter, fast enough to move. */
const POLL_INTERVAL_MS = 3000;

/** Recompute the elapsed label about once a second while the block is up. */
function useElapsedMinutes(since: number | null): number {
    const [minutes, setMinutes] = useState(0);

    useEffect(() => {
        if (since === null) {
            setMinutes(0);
            return;
        }
        const tick = () => setMinutes(Math.floor((Date.now() - since) / 60000));
        tick();
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [since]);

    return minutes;
}

/** Stop the page behind the overlay scrolling while it is up. */
function useBodyScrollLock(active: boolean): void {
    useEffect(() => {
        if (!active) return;
        document.body.classList.add(BODY_LOCK_CLASS);
        return () => document.body.classList.remove(BODY_LOCK_CLASS);
    }, [active]);
}

/**
 * Keep progress current while the lock is up, and take the lock down when the
 * rename finishes.
 *
 * `GET /tenants/me` is the ONLY authority. There used to be a second one — "any
 * successful response means we are back" — and it was wrong, because a lockdown
 * is not all-or-nothing: exempt endpoints, non-tenant-scoped endpoints and staff
 * callers all keep getting 200s while tenant-scoped calls are refused. Those 200s
 * tore the overlay down as fast as the refusals put it up, so on the product the
 * lock never visibly appeared.
 *
 * A failed poll keeps the lock, because the lock is the safe state and dropping
 * it over one lost race flashes the unusable app back at the user. The exception
 * is a poll that cannot ever succeed — 401/403/404 means this caller has no
 * readable tenant, so we can no longer observe the rename at all. Holding the app
 * hostage on a question we cannot ask is worse than letting it through: the
 * refusals will put the lock straight back if the tenant really is still offline.
 */
function useRenameProgressPoll(active: boolean): void {
    const startBlocking = useTenantRenameStore((state) => state.startBlocking);
    const clearBlocking = useTenantRenameStore((state) => state.clearBlocking);
    const inFlight = useRef(false);

    const poll = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        try {
            const tenant = (await tenantService.getMe()) as unknown as {
                slug_rename?: Record<string, unknown> | null;
            };
            if (tenant?.slug_rename) {
                startBlocking(parseTenantRenameInfo(tenant.slug_rename));
                return;
            }
            // The rename is over. Taking the cover off is not enough: every query
            // underneath it failed while the tenant was offline and TanStack holds
            // those errors, so the user would be handed the broken screen the
            // overlay had been hiding and read it as "still broken". Refetch
            // everything, THEN uncover.
            await queryClient.invalidateQueries();
            clearBlocking();
        } catch (error) {
            const status = (error as { status?: number } | null)?.status;
            if (status === 401 || status === 403 || status === 404) {
                // Unobservable, not "still locked" — see the docstring.
                clearBlocking();
            }
            // Anything else (network blip, 5xx, a refusal) keeps the lock.
        } finally {
            inFlight.current = false;
        }
    }, [startBlocking, clearBlocking]);

    useEffect(() => {
        if (!active) return;
        void poll();
        const id = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [active, poll]);
}

/**
 * The bar.
 *
 * Determinate when the job measured its work and indeterminate when it has not
 * yet — a bar that sits at 0% and a bar that is still counting look identical to
 * a user, so the second one animates instead of lying about being stuck.
 */
function RenameProgressBar({ rename }: { rename: TenantRenameInfo }) {
    const { t } = useTranslation();
    const determinate = rename.total > 0 || rename.phasesDone > 0;
    const percent = determinate ? rename.percent : 0;

    return (
        <div className="tenant-rename-progress">
            <div
                className={
                    determinate
                        ? 'tenant-rename-progress-track'
                        : 'tenant-rename-progress-track is-indeterminate'
                }
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={determinate ? percent : undefined}
                aria-label={t('tenantRename.progressLabel')}
            >
                <div
                    className="tenant-rename-progress-fill"
                    style={determinate ? { width: `${percent}%` } : undefined}
                />
            </div>
            <div className="tenant-rename-progress-meta">
                <span>{determinate ? `${percent}%` : t('tenantRename.measuring')}</span>
                {rename.phasesTotal > 0 ? (
                    <span>
                        {t('tenantRename.step', {
                            current: Math.min(rename.phasesDone + 1, rename.phasesTotal),
                            total: rename.phasesTotal,
                        })}
                    </span>
                ) : null}
            </div>
        </div>
    );
}

export function TenantRenameNotice() {
    const { t } = useTranslation();
    const rename = useTenantRenameStore((state) => state.rename);
    const blockedSince = useTenantRenameStore((state) => state.blockedSince);
    const elapsedMinutes = useElapsedMinutes(blockedSince);
    useBodyScrollLock(rename !== null);
    useRenameProgressPoll(rename !== null);

    if (!rename) return null;

    const slugChange =
        rename.oldSlug && rename.newSlug ? `${rename.oldSlug} → ${rename.newSlug}` : null;
    // Phase names are backend identifiers (`content_meta_id`); translate the ones
    // we know and fall back to the raw name rather than hiding an unknown step.
    const phaseLabel = rename.phase
        ? t(`tenantRename.phases.${rename.phase}`, rename.phase)
        : null;
    const rowsDone = rename.total > 0 || rename.examined > 0;

    return (
        <div
            className="tenant-rename-overlay"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="tenant-rename-title"
            aria-describedby="tenant-rename-message"
        >
            <div className="tenant-rename-panel">
                <span className="tenant-rename-spinner" aria-hidden="true" />
                <h2 id="tenant-rename-title" className="tenant-rename-title">
                    {t('tenantRename.label')}
                </h2>
                <p id="tenant-rename-message" className="tenant-rename-message">
                    {t('tenantRename.message')}
                </p>

                <RenameProgressBar rename={rename} />

                <dl className="tenant-rename-facts">
                    {slugChange ? (
                        <>
                            <dt>{t('tenantRename.slugChange')}</dt>
                            <dd>{slugChange}</dd>
                        </>
                    ) : null}
                    {phaseLabel ? (
                        <>
                            <dt>{t('tenantRename.phase')}</dt>
                            <dd>{phaseLabel}</dd>
                        </>
                    ) : null}
                    {rowsDone ? (
                        <>
                            <dt>{t('tenantRename.rows')}</dt>
                            <dd>
                                {rename.total > 0
                                    ? t('tenantRename.rowsValue', {
                                          done: Math.min(rename.examined, rename.total),
                                          total: rename.total,
                                      })
                                    : t('tenantRename.rowsValueUnknownTotal', {
                                          done: rename.examined,
                                      })}
                            </dd>
                        </>
                    ) : null}
                    {elapsedMinutes > 0 ? (
                        <>
                            <dt>{t('tenantRename.elapsed')}</dt>
                            <dd>{t('tenantRename.elapsedValue', { count: elapsedMinutes })}</dd>
                        </>
                    ) : null}
                </dl>

                <p className="tenant-rename-hint">{t('tenantRename.hint')}</p>
            </div>
        </div>
    );
}

export default TenantRenameNotice;
